import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Calendar, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

export default function GoogleCalendarSync({ userId }) {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const { toast } = useToast();

  const checkConnection = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/google-calendar/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setConnectionStatus(data.data);
      }
    } catch (error) {
      console.error("Error checking Google Calendar connection:", error);
      setConnectionStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkConnection();
    
    // Check for OAuth callback parameters
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_calendar_connected') === 'true') {
      toast({
        description: "✅ Google Calendar connected successfully!"
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      checkConnection();
    } else if (params.get('google_calendar_error')) {
      toast({
        variant: "destructive",
        description: `Failed to connect Google Calendar: ${params.get('google_calendar_error')}`
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/google-calendar/connect`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.data.authorization_url) {
        // Debug: Log the URL
        console.log("🔗 Google OAuth URL:", data.data.authorization_url);
        
        // Redirect to Google OAuth
        window.location.href = data.data.authorization_url;
      } else {
        toast({
          variant: "destructive",
          description: "Failed to initiate Google Calendar connection"
        });
      }
    } catch (error) {
      console.error("Error connecting to Google Calendar:", error);
      toast({
        variant: "destructive",
        description: "Failed to connect to Google Calendar"
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectClick = () => {
    setShowDisconnectDialog(true);
  };

  const handleDisconnectConfirm = async () => {
    try {
      setDisconnecting(true);
      setShowDisconnectDialog(false);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/google-calendar/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          description: "Google Calendar disconnected successfully"
        });
        checkConnection();
      } else {
        toast({
          variant: "destructive",
          description: "Failed to disconnect Google Calendar"
        });
      }
    } catch (error) {
      console.error("Error disconnecting Google Calendar:", error);
      toast({
        variant: "destructive",
        description: "Failed to disconnect Google Calendar"
      });
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="mr-2 h-5 w-5" />
            Google Calendar Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Calendar className="mr-2 h-5 w-5" />
          Google Calendar Integration
        </CardTitle>
        <CardDescription>
          {connectionStatus?.connected 
            ? "Your calendar is synced with Google Calendar" 
            : "Connect to sync appointments with Google Calendar"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Connection Status */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center space-x-3">
            {connectionStatus?.connected ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">Connected</p>
                  {connectionStatus.email && (
                    <p className="text-sm text-gray-500">{connectionStatus.email}</p>
                  )}
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium">Not Connected</p>
                  <p className="text-sm text-gray-500">Appointments won't sync to your calendar</p>
                </div>
              </>
            )}
          </div>
          <Badge variant={connectionStatus?.connected ? "default" : "secondary"}>
            {connectionStatus?.connected ? "Active" : "Inactive"}
          </Badge>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {connectionStatus?.connected ? (
            <Button 
              variant="outline" 
              onClick={handleDisconnectClick}
              disabled={disconnecting}
              className="w-full"
            >
              {disconnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                "Disconnect Google Calendar"
              )}
            </Button>
          ) : (
            <Button 
              onClick={handleConnect}
              disabled={connecting}
              className="w-full"
            >
              {connecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Calendar className="mr-2 h-4 w-4" />
                  Connect Google Calendar
                </>
              )}
            </Button>
          )}
        </div>

        {/* Info */}
        <div className="text-sm text-gray-600 p-3 bg-blue-50 rounded-lg">
          <p className="font-medium mb-1">ℹ️ How it works:</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>New appointments automatically sync to your Google Calendar</li>
            <li>Updates and deletions are synced in real-time</li>
            <li>Appointments are always saved to your CRM (sync is optional)</li>
            <li>You can disconnect anytime without losing CRM data</li>
          </ul>
        </div>
      </CardContent>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Calendar?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect Google Calendar? Future appointments will not sync to your calendar.
              Your existing CRM data will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnectConfirm}>
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
